import {
  AdminTaoRoutingTable,
  NonAdminTaoRoutingTable,
} from "@/components/TaoRoutingTable";
import { styled, globalCss } from "@stitches/react";
import { Button, Modal, Input, Banner, BannerItem } from "@Forge/dist";
import { MouseEventHandler, ReactElement, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TaoRulesFilters from "@/components/TaoRuleFilters";
import { useTaoRules } from "@/hooks/use-tao-rules";
import Loader from "@/components/Loader";
import { useTaoUser } from "@/hooks/use-tao-user";
import { TaoUserManager, TaoHistoryManager } from "@/components/TaoUserManager";
import { useAddBucket } from "@/hooks/use-add-new-bucket";

const globalStyles = globalCss({
  '[data-testid="overall-tao-history-modal"] .fe_c_modal__dialog--large': {
    width: "896px !important",
    maxWidth: "90vw !important",
    marginLeft: "auto !important",
    marginRight: "auto !important",
    left: "0 !important",
    right: "0 !important",
  },
});
globalStyles();
const Wrapper = styled("div", {
  display: "flex",
  flexDirection: "column",
  overflowX: "scroll",

  "& .c-eWLVfa": {
    paddingLeft: "8px",
    paddingRight: "16px",
    backgroundColor: "rgb(243, 243, 243)",
    height: "70px",
    marginBottom: "10px",
  },
});

const TaoUserManagerContent = styled("div", {
  maxHeight: "50vh",
  overflowY: "scroll",
  marginBottom: "10px",
});

const CreateBucketButtonWrapper = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "5px",
  marginTop: "15px",
});

const UserActionsWrapper = styled("div", {
  display: "flex",
  marginBottom: "18px",
  gap: "27.9px",
  width: "100%",
  paddingRight: "32px",
  justifyContent: "right",

  "& a:hover": {
    borderBottom: "none",
    transition: "none",
  },
  "& a": {
    borderBottom: "none",
    transition: "none",
  },
});

const TaoUserManagerButtonWrapper = styled("div", {
  display: "flex",
  justifyContent: "right",
});

const BannerWrapper = styled("div", {
  marginBottom: "15px",

  "& .banner": {
    width: "100%",
    top: "1px",
    left: "-2px",
    gap: "0px",
    opacity: "0px",
    position: "relative",
    boxShadow: "0px 0px 8px rgba(0, 0, 0, 0.25)",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    textAlign: "left",
    fontSize: "18px",
    color: "#118647",
    fontFamily: "Source Sans Pro",
    border: "0px 0px 100px 8px",
    borderRadius: "4px",
  },
});

const UserSettingsWrapper = styled("div", {
  display: "flex",
  marginLeft: "32px",
});

const BannerContentWrapper = styled("div", {
  "& .bannerContent": {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: "16px",
    gap: "16px",
  },
  "& .content": {
    alignSelf: "stretch",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "8px",
  },
  "& .contentHeader": {
    alignSelf: "stretch",
    position: "relative",
    lineHeight: "28px",
    fontWeight: 600,
    display: "inline-block",
    height: "23px",
    flexShrink: 0,
  },
  "& .contentBody": {
    alignSelf: "stretch",
    position: "relative",
    fontSize: "16px",
    lineHeight: "20px",
    color: "#5d5e5e",
    whiteSpace: "pre-wrap",
  },
});

const TableWrapper = styled("div", {
  padding: "0 16px",
  display: "flex",
  marginBottom: "50px",
  width: "100%",
  justifyContent: "center",
  alignItems: "center",

  "& p": {
    marginTop: "25px",
    fontWeight: "bold",
  },
});

const TaoRouting = () => {
  const [BannerElement, setBannerElement] = useState<ReactElement | null>(null);
  const [modal, setModal] = useState(false);
  const [confirmationModal, setconfirmationModal] = useState(false);
  const [taoUserManagementModal, setTaoUserManagementModal] = useState(false);
  const [overallTaoHistoryModal, setOverallTaoHistoryModal] = useState(false);
  const [showTaoHistoryByIdModal, setShowTaoHistoryByIdModal] = useState(false);
  const [bucketName, setBucketName] = useState<string>();
  const [selectedTaoId, setSelectedTaoId] = useState(0);
  const navigate = useNavigate();
  const {
    data = {
      taoRulesResponseList: [],
      totalCount: 0,
      totalPages: 0,
    },
    isPending,
    isRefetching,
  } = useTaoRules();
  const {
    data: taoUser,
    isPending: isTaoUsersPending,
  } = useTaoUser();
  const { addBucket } = useAddBucket();

  useEffect(() => {
    if (data === null) {
      navigate(`/error`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const allBanners = [
    {
      id: 0,
      banner: (
        <BannerWrapper className="banner">
          <Banner
            alertType="success"
            onHide={() => setBannerElement(null)}
            id="banner"
            data-testid="success-banner"
          >
            <BannerContentWrapper
              id="banner-item-wrapper"
              className="bannerContent"
            >
              <BannerItem
                style={{ color: "#118647" }}
                className="contentHeader"
              >
                Success
              </BannerItem>
              <BannerItem id="banner-item" className="contentBody">
                Bucket added successfully!
              </BannerItem>
            </BannerContentWrapper>
          </Banner>
        </BannerWrapper>
      ),
    },
    {
      id: 1,
      banner: (
        <BannerWrapper>
          <Banner
            alertType="critical"
            onHide={() => setBannerElement(null)}
            id="banner"
            className="banner"
            data-testid="success-banner"
          >
            <h3>Success!</h3>
            <BannerContentWrapper id="banner-item-wrapper">
              <BannerItem id="banner-item">
                Bucket added successfully!
              </BannerItem>
            </BannerContentWrapper>
          </Banner>
        </BannerWrapper>
      ),
    },
    {
      id: 2,
      banner: (
        <BannerWrapper className="banner">
          <Banner
            alertType="success"
            onHide={() => setBannerElement(null)}
            id="banner"
            data-testid="success-banner"
          >
            <BannerContentWrapper
              id="banner-item-wrapper"
              className="bannerContent"
            >
              <BannerItem
                style={{ color: "#118647" }}
                className="contentHeader"
              >
                Success
              </BannerItem>
              <BannerItem id="banner-item" className="contentBody">
                User Access Removed
              </BannerItem>
            </BannerContentWrapper>
          </Banner>
        </BannerWrapper>
      ),
    },
  ];

  const showBucketModal = () => {
    setModal(true);
  };
  const showConfirmationModal = () => {
    setModal(false);
    setconfirmationModal(true);
  };
  const newBucketSubmit: MouseEventHandler<HTMLButtonElement> = () => {
    showConfirmationModal();
  };
  const newBucketConfirmationBanners = () => {
    setconfirmationModal(false);
    if (bucketName) addBucket({ bucketName: bucketName, isEnabled: true });
    // return setBannerElement(allBanners[0].banner);
  };
  const showTaoManagementModal = () => {
    setTaoUserManagementModal(true);
  };
  const showOverallTaoHistoryModal = () => {
    setOverallTaoHistoryModal(true);
  };

  const handleSelectedTaoId = (data: number) => {
    setSelectedTaoId(data);
    setShowTaoHistoryByIdModal(true);
  };

  const isTaoAdmin = taoUser?.isTaoAdmin;

  return (
    <Wrapper>
      {BannerElement}
      {isTaoAdmin ? (
        <UserSettingsWrapper>
          <Button
            variant="secondary"
            data-testid="tao-user-settings-button"
            onClick={showTaoManagementModal}
          >
            TAO User Settings
          </Button>
        </UserSettingsWrapper>
      ) : (
        <></>
      )}
      <TaoRulesFilters
        showTaoHistoryByIdModal={showTaoHistoryByIdModal}
        setShowTaoHistoryByIdModal={setShowTaoHistoryByIdModal}
        selectedTaoHistoryById={selectedTaoId}
        isTaoAdmin={isTaoAdmin}
      />
      <UserActionsWrapper>
        {isTaoAdmin ? (
          <>
            <Button
              variant="secondary"
              data-testid="add-bucket-button"
              onClick={showBucketModal}
            >
              Add Bucket
            </Button>
            <Button
              variant="secondary"
              data-testid="create-new-tao-button"
              onClick={() =>
                navigate("/tao-routing/tao-editor?currentStep=departments")
              }
            >
              Create New TAO
            </Button>
            <Button
              variant="secondary"
              data-testid="view-change-history-button"
              onClick={showOverallTaoHistoryModal}
            >
              View Change History
            </Button>
          </>
        ) : (
          <></>
        )}
      </UserActionsWrapper>
      <Modal
        className="userManagementModal"
        data-testid="user-manager-modal"
        headerText="TAO User Settings"
        show={taoUserManagementModal}
        includeButtonRow={false}
        width="large"
        onHide={() => {
          setTaoUserManagementModal(false);
        }}
        onBackdropClick={() => {
          setTaoUserManagementModal(false);
        }}
        onPrimaryClick={() => {
          setTaoUserManagementModal(false);
        }}
        primaryButtonText="Done"
      >
        <TaoUserManagerContent>
          <TaoUserManager
            setBannerElement={setBannerElement}
            allBanners={allBanners}
          />
        </TaoUserManagerContent>
        <TaoUserManagerButtonWrapper>
          <Button
            onClick={() => {
              setTaoUserManagementModal(false);
            }}
          >
            Done
          </Button>
        </TaoUserManagerButtonWrapper>
      </Modal>

      <Modal
        data-testid="overall-tao-history-modal"
        headerText="Overall TAO Change History"
        show={overallTaoHistoryModal}
        includeButtonRow={false}
        width="large"
        onHide={() => {
          setOverallTaoHistoryModal(false);
        }}
        onBackdropClick={() => {
          setOverallTaoHistoryModal(false);
        }}
        onPrimaryClick={() => {
          setOverallTaoHistoryModal(false);
        }}
      >
        <TaoHistoryManager />
      </Modal>

      <Modal
        data-testid="bucket-modal"
        headerText="Add a New Bucket"
        primaryButtonText="Create New Bucket"
        show={modal}
        includeButtonRow={false}
        onPrimaryClick={newBucketSubmit}
        onHide={() => {
          setModal(false);
          setBucketName(undefined);
        }}
        onBackdropClick={() => {
          setModal(false);
          setBucketName(undefined);
        }}
      >
        <p>Add New Bucket</p>
        <Input
          placeholder="Type"
          type="text"
          name="bucketName"
          data-testid="bucket-modal-input"
          onChange={(e) => setBucketName(e.target.value)}
        />
        <CreateBucketButtonWrapper>
          <Button
            variant="secondary"
            onClick={() => {
              setModal(false);
              setBucketName(undefined);
            }}
          >
            Cancel
          </Button>
          <Button onClick={newBucketSubmit} disabled={bucketName == undefined}>
            Create New Bucket
          </Button>
        </CreateBucketButtonWrapper>
      </Modal>
      <Modal
        data-testid="confirmation-modal"
        headerText="Create A New Bucket?"
        alertType="attention"
        primaryButtonText="Create New Bucket"
        show={confirmationModal}
        onPrimaryClick={newBucketConfirmationBanners}
        onHide={() => {
          setconfirmationModal(false);
          setBucketName(undefined);
        }}
        onBackdropClick={() => {
          setconfirmationModal(false);
          setBucketName(undefined);
        }}
      >
        <p>Are you sure you want to add a new bucket?</p>
      </Modal>
      <Loader
        displayText="Loading..."
        isLoading={isPending || isTaoUsersPending || isRefetching}
      >
        <TableWrapper>
          {data.taoRulesResponseList == null ? (
            <p>No Tao Rules match specified filter values</p>
          ) : isTaoAdmin ? (
            <AdminTaoRoutingTable
              setSelectedTaoId={handleSelectedTaoId}
              rows={data?.taoRulesResponseList}
            />
          ) : (
            <NonAdminTaoRoutingTable
              setSelectedTaoId={handleSelectedTaoId}
              rows={data?.taoRulesResponseList}
            />
          )}
        </TableWrapper>
      </Loader>
    </Wrapper>
  );
};

export default TaoRouting;
